/**
 * EventBusService — ORCH-1.
 *
 * Central publish/subscribe orchestrator for the Kairos platform.
 * Every cross-module state change routes through this service.
 * No module calls another module's service directly across module boundaries.
 *
 * All Firestore writes go through DataService.js — never Firebase directly.
 * tenantId is required on every operation.
 *
 * Storage layout:
 *   events_log/{tenantId}/events/{eventId}  — append-only event ledger
 *   event_topics/{eventType}                — topic registry (DOC-1 reads this)
 */

import DataService from './DataService';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';

// CRM-PERFORMANCE-BASELINE-1: emit→CF-ack latency.
//
// PERF-FEEDBACK-LOOP-FIX (2026-05-18): disabled. This recorded an 'eventbus'
// metric on every publish — which itself wrote performance_metrics. With the
// recordMetric → publish('perf.metric.recorded') chain removed (see
// PerformanceService.recordMetric), the eventbus metric is the last remaining
// path that turns "publish one event" into "write a perf row every time."
// On a busy surface this guarantees a perf_metrics write per publish per
// keystroke / poll tick / etc., which is exactly the meltdown the operator
// reported. Re-enable only when a sampling / batching layer lands.
//
// Kept as a no-op so callers don't have to change.
function _recordEventBusPerf(_startMs) { /* intentionally no-op */ }

// ULID implementation — lexicographically sortable, collision-resistant IDs.
// Encodes 48-bit ms timestamp + 80 bits of randomness in Crockford base-32.
const ENCODING = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
function generateULID() {
  const now = Date.now();
  let ulid = '';
  // 10-char timestamp prefix (48 bits)
  let t = now;
  for (let i = 9; i >= 0; i--) {
    ulid = ENCODING[t & 31] + ulid;
    t = Math.floor(t / 32);
  }
  // 16-char random suffix (80 bits)
  for (let i = 0; i < 16; i++) {
    ulid += ENCODING[Math.floor(Math.random() * 32)];
  }
  return ulid;
}

const REQUIRED_FIELDS = ['eventType', 'tenantId', 'sourceModule', 'sourceId', 'payload', 'actor'];
const VALID_MODULES = [
  'ims', 'tms', 'wms', 'cx', 'fin', 'fleet', 'iot', 'agent', 'orch', 'system',
  'cruise', 'commerce', 'net', 'comms', 'it', 'argus', 'ktrust',
  // ELYON-AEGIS-SUBSTRATE-REPAIR-1: sentinel substrate + calibration service
  'sentinel', 'agent-calibration',
  'kfol-mar', // retained for historical events; canonical name is fleet-market
  'fleet-market', 'fleet-market-dashboard', 'fleet-market-signals',
  'fleet-fraud', 'fleet-invoicing-fx', 'fleet-invoicing-tax', 'fleet-invoicing-settlement',
  'nav', 'test-chaos', 'platform', 'cog-council', 'demo', 'lrg', 'driver',
  'counsel', 'kxol', 'kcol',
  // EMBED-1 (2026-04-29) — Wave KB-A substrate modules
  'embedding',   // Voyage-3 embedding substrate — emits embedding.usage.recorded
  // KBRAIN-LOG-1 (2026-04-29) — observation-grounded supervision corpus
  'kbrain',      // KBRAIN observation substrate — emits kbrain.observation.recorded
  // TS-1 (2026-04-29) — Chronos-Bolt forecasting substrate
  'timeseries',  // TimeSeries forecasting substrate — emits timeseries.forecast.* events
  // GEO-1 (2026-04-29) — XGBoost route scoring substrate (first KBRAIN model w/ weights)
  'geo',         // Route scoring substrate — emits geo.route.scored
  // FIN-2-EXTENSIONS (N23, 2026-04-30) — KFOL ledger consumer namespace
  'kfol',        // KFOL ledger consumer — consumes fin.ledger.entry.posted
  // NCL-REAL-INTEGRATION (N23 Seat 3) — NCL lighthouse pilot adapter module // foundation-purity-allow: session breadcrumb in module registry; NCL identifies the lighthouse pilot session, not a hardcoded tenant
  'ncl',         // NCL cruise pilot adapters — emits ncl.tender.received, ncl.tender.acknowledged // foundation-purity-allow: module namespace for NCL pilot adapter; NCL is the registered module prefix per ORCH-1
  // WMS-LIGHTHOUSE-CLUSTER (N24 Seat 1) — KWOL warehouse orchestration substrate
  'kwol',        // KWOL warehouse orchestration — emits kwol.task.dispatched, kwol.robot.assigned
  // CRM-4a (N24, 2026-04-30) — Sending Infrastructure Substrate
  'crm',         // CRM outreach + inbound substrate — emits outreach.send.dispatched,
                 // inbound.icc.session.completed, inbound.icc.abstention,
                 // inbound.specialist.run.*, inbound.discovery.*, inbound.proposal.*,
                 // inbound.negotiation.*, inbound.qualification.*  (CRM-VIS-3, Wave 28)
                 // crm.memory.updated (CRM-USER-MEMORY-1 W27 P0 — cross-conversation freight-vertical user memory substrate)
  // LTL-RATING-FABRIC-1 (Wave 28) — provider-agnostic LTL rating/dispatch/track fabric
  'ltl',         // LTL fabric — emits ltl.rate.requested, ltl.rate.returned,
                 // ltl.dispatch.confirmed, ltl.tracking.updated, ltl.adapter.credentials.missing
  // AOP-FOUNDATION-1 (Wave 27 P0) — Agent Operating Procedure substrate
  'aop',         // AOP substrate — emits procedure.execution.started, .completed, .failed, .partial-result
  // SEAT-119 INTERNAL-DEVELOPER-PLATFORM-IDP (OVERNIGHT-BATCH-v13-2026-05-08)
  'idp',         // IDP substrate — emits infra.dev-env.provisioned, infra.resource.requested,
                 // infra.deploy.initiated, dora.metrics.calculated
  // SYNTHETIC-ENTITY-GENERATION-SUBSTRATE-1 (W27) — 1A Moat synthetic entity generation
  'synthetic',   // Synthetic entity substrate — emits synthetic.entity.generated, synthetic.proof.issued
                 // consumes flywheel.insight.derived (via FlywheelDistributionConsumer)
  // DATA-FLYWHEEL-FOUNDATION-1 (W27) — 1A Moat cross-tenant learning substrate
  'flywheel',    // emits flywheel.signal.ingested, flywheel.aggregate.ready,
                 // flywheel.insight.derived, flywheel.boundary.verified, flywheel.audit.signed
  // CROSS-TENANT-LEARNING-PRIVACY-1 (W27) — privacy verification + enforcement layer
  'privacy',     // emits privacy.aggregate.verified, privacy.audit.complete, privacy.budget.verified
  // BOARDROOM-MODE-1 (W27 S01, 2026-05-12) — acquirer/investor-facing constellation surface
  'boardroom',   // emits boardroom.view.opened.v1, boardroom.lens.toggled.v1
  // ARCHIMEDES-1 (W27 S10, 2026-05-12) — operator coaching agent
  'archimedes',  // emits archimedes.question.asked, archimedes.suggestion.fired, archimedes.suggestion.dismissed
  // MERCURYGATE-MIGRATION-1 (W27 S12, 2026-05-12) — MercuryGate TMS migration tooling
  'migration',   // emits MERCURYGATE-EXTRACT-STARTED, MERCURYGATE-MIGRATION-PROGRESS, MERCURYGATE-MIGRATION-COMPLETED (CF-only via EventBusPublisher)
  // TMS-CARRIER-VETTING-SURFACE-1 (W28 P0, 2026-05-14) — KSFD carrier compliance substrate
  'ksfd',        // emits ksfd.carrier.vetting_complete (carrier compliance, capacity, safety scores)
  // INTL-CT-1 (W28, 2026-05-13) — Cross-border load management + CBP/DCSA interop
  // INTL-FSMA-204-1 (Phase 1 P0, 2026-05-14) — FSMA 204(d) traceability
  'intl',        // emits intl.load.cbp_cleared, intl.load.border_crossing_completed,
                 //        intl.fsma.cte.captured, intl.fsma.recall.triggered
                 // consumes krol.route.cross_border_recommended, intl.dcsa.container_event
  // TMS-MOBILE-1 (W28 P0, 2026-05-13) — Driver mobile app + voice status reporting + parcel tracking
  'tms-mobile',  // emits tms.mobile.load_status_reported, tms.mobile.pickup_confirmed, tms.mobile.delivery_confirmed
  // TMS-TRACK-TRACE-AUTOMATION-1 (W28 P0, 2026-05-13) — KROL route prediction substrate
  'krol',        // emits krol.route.prediction_delivered; consumed by ETASyncService (TMS track-trace)
  // PYLON-3-SRV-1 (W28, 2026-05-13) — Appointment Dashboard service foundation
  'pylon',       // emits appointment:created, appointment:status_changed, appointment:cancelled,
                 // appointment:conflict_detected, appointment:confirmed, slot:released
  // TMS-VOICE-CAPACITY-1 (W29 P1, 2026-05-13) — Bland.ai IVR capacity inquiries + COMMS-HUB
  'carrier',     // emits capacity.inquiry (voice IVR inquiry recorded)
  // TMS-AGENT-PERSONA-LAYER-1 + TMS-AGENT-AUTONOMY-MATRIX-1 (W30, 2026-05-16) — agent org substrate
  'agent-org',   // emits agent-org.persona.upserted, agent-org.autonomy.change.proposed,
                 //        agent-org.autonomy.matrix.updated, agent-org.autonomy.bound.violated
                 // Note: also consumed by agentProvenanceService (10.1) and deptMetricsService (10.2)
  // TMS-ACCESSORIAL-AUTOMATION-1 (W29 P1, 2026-05-16) — FMCSA-default accessorial rules engine
  // TMS-SETTLEMENT-AUTOMATION-1 (W29 P1, 2026-05-16) — Per-carrier settlement + QB/NetSuite + auto-pay
  'kfin',        // emits kfin.accessorial.detected, kfin.accessorial.disputed,
                 //        kfin.accessorial.override_applied,
                 //        kfin.settlement.created, kfin.settlement.paid, kfin.settlement.overdue,
                 //        kfin.settlement.payment_run_created, kfin.dispute.opened, kfin.dispute.resolved
  // HORUS-AGGREGATOR-1 + HORUS-POLICY-ENGINE-1 (W28, 2026-05-17) — meta-orchestration substrate
  'horus',       // emits horus.seat.dispatched, horus.seat.preflight, horus.seat.state-changed,
                 //        horus.addendum.applied, horus.addendum.compiled,
                 //        horus.escalation.raised, horus.escalation.resolved,
                 //        horus.decision.appended, horus.halt.requested,
                 //        horus.killswitch.toggled
  // CRM-COMMS-HUB-1 (W28 P0 Phase B, 2026-05-17) — unified outbound routing engine
  'comms',       // emits comms.outbound.dispatched, comms.outbound.delivered,
                 //        comms.outbound.failed, comms.outbound.opt-out-triggered
                 // consumes crm.user-memory.updated, agent.outbound.dialed
  // KCON-0 (P5-LANE-03, 2026-05-17) — HARMONIA constellation foundation (Kairos Consolidation Orchestration)
  'kcon',        // emits kcon.harmonia.agent_registered, kcon.harmonia.constellation_ready,
                 //        kcon.load_group.formed, kcon.load_group.dissolved,
                 //        kcon.mode_arbitration.completed, kcon.mode_arbitration.plan_recommended,
                 //        kcon.route.multistop_optimized, kcon.route.tsp_solved,
                 //        kcon.crossdock.consolidation_proposed, kcon.crossdock.facility_window_validated
                 // consumes tms.load.tender_received, krol.route.prediction_delivered
  // SUP-ALIGN-1 + KIOL-0 + DP-1-EXT + DP-2-EXT (P5-LANE-04, 2026-05-17) — KIOL inventory orchestration substrate
  'kiol',        // emits kiol.supplier.alignment.created, kiol.supplier.alignment.updated,
                 //        kiol.supplier.lead_time.changed,
                 //        kiol.atp.computed, kiol.atp.below_safety_stock, kiol.atp.negative_flagged,
                 //        kiol.demand.history.updated, kiol.demand.trend.detected,
                 //        kiol.demand.federated.signal_published, kiol.demand.federated.cohort_updated
  // AEGIS-CONVERSATIONAL-UI-1 (W28 P0, 2026-05-24) — conversational UI modification substrate
  'conversational-ui', // emits aegis.conversationalUi.intent_classified, aegis.conversationalUi.change_applied,
                       //        aegis.conversationalUi.proposal_created, aegis.conversationalUi.guardrail_engaged,
                       //        aegis.conversationalUi.aesthetic_feedback_logged
                       // consumes tenant.brand.updated, aegis_authority_changes.created
  // SETTINGS-EVENTBUS-1 (2026-05-29) — EventBus sweep across all SETTINGS screens
  'settings',          // emits settings.branding.saved, settings.branding.logo_uploaded,
                       //        settings.email_branding.saved, settings.email_branding.template_previewed,
                       //        settings.custom_object.created, settings.custom_object.field_added, settings.custom_object.deleted,
                       //        settings.trust_policy.updated, settings.trust_policy.rule_added,
                       //        settings.access_log.exported, settings.access_log.filtered,
                       //        settings.compliance.report_generated
];

// EVENT_CATALOG — documented schema contracts for TMS events emitted by TMS services.
// TMS-EVENTBUS-CONTRACT-1 (2026-05-26): 10 events added — these were emitted by TMS
// services but lacked catalog entries (gap identified by TMS-WORLD-CLASS-AUDIT-1 Seat C).
// Format matches the topic registry schema (see _upsertTopicRegistryFull).
const EVENT_CATALOG = {
  'tms.claims.policy_bound': {
    module: 'tms',
    description: 'Cargo claim bound to an insurance policy; triggers settlement workflow',
    emittedBy: ['ClaimsService'],
    consumedBy: ['fin', 'fleet-invoicing-settlement'],
  },
  'tms.dispute.initiated': {
    module: 'tms',
    description: 'Shipper or carrier initiates a rate or service dispute on a load',
    emittedBy: ['FreightAuditService'],
    consumedBy: ['cx', 'fin'],
  },
  'tms.freight_audit.approved': {
    module: 'tms',
    description: 'Freight audit record approved; triggers invoice finalization',
    emittedBy: ['FreightAuditService'],
    consumedBy: ['fin', 'orch'],
  },
  'tms.invoice.requested': {
    module: 'tms',
    description: 'Invoice generation requested for a completed load',
    emittedBy: ['LoadSettlementService'],
    consumedBy: ['fin'],
  },
  'tms.load.dispatched': {
    module: 'tms',
    description: 'Load dispatched to carrier; driver assignment confirmed',
    emittedBy: ['DispatchEngineService'],
    consumedBy: ['cx', 'orch', 'argus'],
  },
  'tms.load.driver_unassigned': {
    module: 'tms',
    description: 'Driver unassigned from a load; triggers re-dispatch workflow',
    emittedBy: ['DispatchEngineService'],
    consumedBy: ['cx', 'orch'],
  },
  'tms.settlement.approved': {
    module: 'tms',
    description: 'Load settlement approved; triggers payment disbursement',
    emittedBy: ['LoadSettlementService'],
    consumedBy: ['fin', 'fleet-invoicing-settlement'],
  },
  'tms.tender.auto_dispatched': {
    module: 'tms',
    description: 'Tender auto-dispatched to carrier via auto-tender rules engine',
    emittedBy: ['SpotBidAutoAwardService'],
    consumedBy: ['cx', 'orch'],
  },
  'tms.tender.fallback_engaged': {
    module: 'tms',
    description: 'Auto-tender fallback engaged after primary carrier rejection',
    emittedBy: ['SpotBidAutoAwardService'],
    consumedBy: ['cx', 'orch'],
  },
  'tms.tender.rule_matched': {
    module: 'tms',
    description: 'Auto-tender rule matched for a load; rule ID logged in payload',
    emittedBy: ['SpotBidAutoAwardService'],
    consumedBy: ['cx', 'orch', 'argus'],
  },
};

function validateEvent(event) {
  for (const field of REQUIRED_FIELDS) {
    if (event[field] === undefined || event[field] === null || event[field] === '') {
      throw new Error(`EventBusService: missing required field "${field}" on event (type=${event.eventType || 'unknown'})`);
    }
  }
  if (!VALID_MODULES.includes(event.sourceModule)) {
    throw new Error(`EventBusService: invalid sourceModule "${event.sourceModule}". Must be one of: ${VALID_MODULES.join(', ')}`);
  }
  if (typeof event.eventType !== 'string' || !event.eventType.includes('.')) {
    throw new Error(`EventBusService: eventType must be dot-namespaced (e.g. "ims.stock.low"), got "${event.eventType}"`);
  }
  if (typeof event.tenantId !== 'string' || event.tenantId.trim() === '') {
    throw new Error('EventBusService: tenantId must be a non-empty string');
  }
  if (event.confidence !== undefined && (typeof event.confidence !== 'number' || event.confidence < 0 || event.confidence > 1)) {
    throw new Error('EventBusService: confidence must be a number between 0 and 1');
  }
}

// Attempt Pub/Sub delivery via Cloud Function. Fire-and-forget —
// the event is already durable in Firestore; Pub/Sub is best-effort fast delivery.
async function publishToPubSub(eventDoc) {
  try {
    const pubsubUrl = import.meta.env.VITE_PUBSUB_PUBLISH_URL;
    if (!pubsubUrl) return;
    await fetch(pubsubUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event: eventDoc }),
    });
  } catch {
    // Pub/Sub failure is non-fatal — Firestore is the durable record.
    // Failed Pub/Sub deliveries after 5 attempts land in event_delivery_failures/{tenantId}/failures/
    // via DlqMonitor CF (ORCH-1-FOUNDATION). Use replayFromDlq callable to retry.
  }
}

// VIEW-MODE-SWITCH-FIX-1 (2026-05-01): in-process subscriber registry.
//
// Background: useViewMode (and other in-tab consumers like
// PackagingOrchestrator, AdapterHealthDashboard) call
// EventBusService.subscribe(eventType, handler) to react to publishes
// from elsewhere in the same browser tab. Until now the method did not
// exist; useViewMode defended against this with a `typeof !== 'function'`
// guard and silently became a no-op, which meant the App-level
// ViewModeRouter never re-rendered after the switcher modal called
// setUserViewMode — visible bug: switching view mode appears not to work.
//
// Scope: in-process, single-tab. Cross-tab/cross-client delivery still
// needs the server-side Pub/Sub fan-out (handled by publishEvent CF).
// This registry only closes the in-tab subscriber loop the existing call
// sites already assume exists.
const _subscribers = new Map();

function _addSubscriber(eventType, handler) {
  if (!_subscribers.has(eventType)) _subscribers.set(eventType, new Set());
  _subscribers.get(eventType).add(handler);
  return () => {
    const set = _subscribers.get(eventType);
    if (!set) return;
    set.delete(handler);
    if (set.size === 0) _subscribers.delete(eventType);
  };
}

function _dispatchInProcess(eventDoc) {
  const handlers = _subscribers.get(eventDoc.eventType);
  if (!handlers || handlers.size === 0) return;
  for (const handler of handlers) {
    try {
      handler(eventDoc);
    } catch (err) {
      // A misbehaving subscriber must not break the publisher or other
      // subscribers. Log and continue.
      console.warn(
        `[EventBus] Subscriber for "${eventDoc.eventType}" threw:`,
        err?.message || err,
      );
    }
  }
}

const EventBusService = {
  /**
   * Publish an event to the bus.
   *
   * Validates schema, assigns ULID eventId, writes to
   * events_log/{tenantId}/events/{eventId} via DataService, then
   * fans out to Pub/Sub for subscriber delivery.
   *
   * Returns the eventId.
   */
  async publish(event) {
    validateEvent(event);
    const _t0 = event.eventType !== 'perf.metric.recorded' ? performance.now() : null;

    const eventId = generateULID();
    const correlationId = event.correlationId || eventId;

    const eventDoc = {
      eventId,
      eventType: event.eventType,
      tenantId: event.tenantId,
      sourceModule: event.sourceModule,
      sourceId: String(event.sourceId),
      payload: event.payload || {},
      causationId: event.causationId || null,
      correlationId,
      confidence: typeof event.confidence === 'number' ? event.confidence : 1.0,
      actor: event.actor,
      version: event.version || '1.0',
      // timestamp is set by DataService via serverTimestamp()
    };

    // EVENTBUS-CLIENT-CALLABLE-BRIDGE-1 (2026-05-01):
    // Direct Firestore write to events_log was always rejected by rules
    // (`allow create: if false` — server-only audit ledger). Now routes
    // through the publishEvent callable which writes via Admin SDK and
    // handles Pub/Sub fan-out server-side. Client no longer touches
    // events_log directly.
    if (!functions) {
      throw new Error('EventBusService: Firebase Functions not configured. Cannot publish event.');
    }
    const publishEventFn = httpsCallable(functions, 'publishEvent');
    await publishEventFn(eventDoc);
    if (_t0 !== null) _recordEventBusPerf(_t0);

    // VIEW-MODE-SWITCH-FIX-1: dispatch to in-process subscribers AFTER the
    // server-side write has been acknowledged. Local subscribers see only
    // events that were durably persisted, which keeps in-tab UI state
    // consistent with the events_log audit ledger.
    _dispatchInProcess(eventDoc);

    // Upsert topic registry — non-blocking, best-effort
    // ORCH-1-FOUNDATION: passes full event for payloadVersion + latestPayloadShape capture
    this._upsertTopicRegistryFull(event).catch(() => {});

    return eventId;
  },

  /**
   * Subscribe to events of `eventType` published in the current tab.
   *
   * Supports two arg shapes:
   *   subscribe('view_mode.changed', handler) → unsubFn
   *   subscribe({ eventType, handler, subscriberId?, tenantId? }) → unsubFn
   *
   * Returns an unsubscribe function. Handlers are invoked synchronously
   * after the publish callable resolves; thrown handler errors are caught
   * so one bad subscriber cannot break the bus or its peers.
   *
   * Cross-tab / cross-client delivery is the responsibility of the
   * server-side Pub/Sub fan-out wired in publishEvent (PR 560). This // types-boundary-allow
   * subscribe path is in-process only.
   */
  subscribe(eventTypeOrConfig, maybeHandler) {
    let eventType, handler;
    if (typeof eventTypeOrConfig === 'string') {
      eventType = eventTypeOrConfig;
      handler = maybeHandler;
    } else if (eventTypeOrConfig && typeof eventTypeOrConfig === 'object') {
      eventType = eventTypeOrConfig.eventType;
      handler = eventTypeOrConfig.handler;
    }
    if (!eventType || typeof handler !== 'function') return () => {};
    return _addSubscriber(eventType, handler);
  },

  /**
   * Retrieve the full ordered event chain for a workflow thread.
   * Returns events_log entries sorted ascending by timestamp (causal order).
   *
   * ORCH-1-REPLAY: uses DataService.queryEventsForCorrelation which queries
   * the (correlationId ASC, createdAt ASC) composite index directly — no
   * full-collection scan.
   */
  async replay(correlationId, tenantId) {
    if (!correlationId || !tenantId) {
      throw new Error('EventBusService.replay: correlationId and tenantId are required');
    }
    return DataService.queryEventsForCorrelation(correlationId, tenantId);
  },

  /**
   * Walk causationId links backward from a given eventId to return
   * the full ancestry array, root first.
   *
   * ORCH-1-REPLAY: no longer performs a full collection scan.
   * Fetches the target event by ID to obtain its correlationId, then
   * queries only events in the same correlation thread via the composite
   * index before walking the causation chain.
   */
  async getEventChain(eventId, tenantId) {
    if (!eventId || !tenantId) {
      throw new Error('EventBusService.getEventChain: eventId and tenantId are required');
    }

    const parentPath = 'events_log/' + tenantId + '/events';

    // Step 1: point read on target event — obtains correlationId cheaply.
    const target = await DataService.getSubById(parentPath, eventId);
    if (!target) return [];

    // Step 2: query only events sharing this correlation thread (uses index).
    const correlationId = target.correlationId || eventId;
    const siblings = await DataService.queryEventsForCorrelation(correlationId, tenantId);

    const byId = {};
    for (const e of siblings) {
      const key = e.eventId || e.id;
      if (key) byId[key] = e;
    }

    // Step 3: walk causation chain backward from target, root first.
    const chain = [];
    let current = byId[eventId] || target;
    const visited = new Set();
    while (current) {
      const key = current.eventId || current.id;
      if (!key || visited.has(key)) break;
      visited.add(key);
      chain.unshift(current);
      if (!current.causationId) break;
      current = byId[current.causationId];
    }

    return chain;
  },

  /**
   * ORCH-1-REPLAY: Cursor-based pagination over a correlationId event chain.
   * Designed for DOC-1 Evidence View — avoids loading the full chain into
   * memory when a correlation thread spans thousands of events.
   *
   * @param {string} correlationId
   * @param {string} tenantId
   * @param {{ cursor?: any, pageSize?: number }} opts
   *   cursor   — opaque cursor value from the previous page's `nextCursor`
   *              (Firestore Timestamp or numeric seconds). Null = first page.
   *   pageSize — max events per page (default 25).
   * @returns {Promise<{ events: object[], nextCursor: any, hasMore: boolean }>}
   */
  async getEventChainPage(correlationId, tenantId, { cursor = null, pageSize = 25 } = {}) {
    if (!correlationId || !tenantId) {
      throw new Error('EventBusService.getEventChainPage: correlationId and tenantId are required');
    }
    const { docs, nextCursor, hasMore } = await DataService.queryEventChainPage(
      correlationId,
      tenantId,
      { cursor, pageSize },
    );
    return { events: docs, nextCursor, hasMore };
  },

  // ── Internal helpers ──────────────────────────────────────────────────

  async _upsertTopicRegistry(eventType, sourceModule) {
    // Document ID uses underscores (dots not allowed in Firestore doc IDs)
    const docId = eventType.replace(/\./g, '_');
    try {
      const existing = await DataService.getById('event_topics', docId, null);
      if (existing) {
        // Update lastSeenAt, add sourceModule if new, and update payload shape metadata.
        // ORCH-1-FOUNDATION: payloadVersion + latestPayloadShape added (W29).
        const publishedBy = existing.publishedBy || [];
        if (!publishedBy.includes(sourceModule)) publishedBy.push(sourceModule);
        // NOTE: _upsertTopicRegistry is called from publish() which already has the event
        // in scope, but this method only receives (eventType, sourceModule). We capture
        // shape metadata by passing the full event at call time via the internal _upsertTopicRegistryFull.
        await DataService.update('event_topics', docId, {
          lastSeenAt: new Date().toISOString(),
          publishedBy,
          updatedAt: new Date().toISOString(),
        }, 'eventbus');
      } else {
        await DataService.create('event_topics', {
          id: docId,
          eventType,
          description: '',
          publishedBy: [sourceModule],
          subscribedBy: [],
          payloadSchema: {},
          examplePayload: {},
          payloadVersion: '1.0',
          latestPayloadShape: [],
          firstSeenAt: new Date().toISOString(),
          lastSeenAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          tenantId: 'system',
        }, 'eventbus', 'eventbus');
      }
    } catch {
      // Registry upsert is best-effort — never block event delivery
    }
  },

  /**
   * ORCH-1-FOUNDATION: Full topic registry upsert — captures payloadVersion
   * and latestPayloadShape (top-level payload key names only; no PII values).
   *
   * Replaces the old _upsertTopicRegistry(eventType, sourceModule) call site
   * on the publish() hot path. The old signature method is kept for callers
   * outside of publish() that still use it.
   */
  async _upsertTopicRegistryFull(event) {
    const { eventType, sourceModule } = event;
    if (!eventType || !sourceModule) return;
    const docId = eventType.replace(/\./g, '_');
    const payloadVersion = event.version || '1.0';
    const latestPayloadShape = Object.keys(event.payload || {});
    try {
      const existing = await DataService.getById('event_topics', docId, null);
      if (existing) {
        const publishedBy = existing.publishedBy || [];
        if (!publishedBy.includes(sourceModule)) publishedBy.push(sourceModule);
        await DataService.update('event_topics', docId, {
          lastSeenAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          publishedBy,
          payloadVersion,
          latestPayloadShape,
        }, 'eventbus');
      } else {
        await DataService.create('event_topics', {
          id: docId,
          eventType,
          description: '',
          publishedBy: [sourceModule],
          subscribedBy: [],
          payloadSchema: {},
          examplePayload: {},
          payloadVersion,
          latestPayloadShape,
          firstSeenAt: new Date().toISOString(),
          lastSeenAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          tenantId: 'system',
        }, 'eventbus', 'eventbus');
      }
    } catch {
      // Registry upsert is best-effort — never block event delivery
    }
  },
};

export default EventBusService;